/* ============================================================
   Product Family Filter Schema Engine — collection page JS
   Progressive enhancement layer. Without JS the page still works:
   - filter inputs live in a GET form posting to the collection URL
   - pills and pagination are plain links
   With JS we add:
   - mobile filter drawer (focus management, Escape to close)
   - auto-apply on input change via fetch + region swap (no reload)
   - back/forward button support via popstate
   - analytics event hooks (see docs/product-family-engine/README.md)
   ============================================================ */
(function () {
  'use strict';

  // The filters and grid sections each emit a <script> tag for this file,
  // and the browser executes one per tag. Without this guard every
  // delegated listener registers twice, so a group-header click toggles
  // open+closed in one go (filters look dead) and fetch/analytics double.
  if (window.__pfFiltersInit) return;
  window.__pfFiltersInit = true;

  var REGION_SELECTOR = '[data-pf-region]';
  if (!document.querySelector(REGION_SELECTOR)) return;

  /* ----------------------------------------------------------
     Analytics: every meaningful interaction emits both a DOM
     CustomEvent ("pf:analytics") and a dataLayer push when a GTM
     dataLayer exists. Wire GA4/segment/etc. by listening for the
     CustomEvent or mapping dataLayer events.
     ---------------------------------------------------------- */
  function track(eventName, detail) {
    detail = detail || {};
    detail.event_name = eventName;
    detail.collection = (document.querySelector('[data-pf-page]') || {}).getAttribute
      ? document.querySelector('[data-pf-page]').getAttribute('data-pf-collection')
      : null;
    detail.product_family = (document.querySelector('[data-pf-page]') || {}).getAttribute
      ? document.querySelector('[data-pf-page]').getAttribute('data-pf-family')
      : null;
    try {
      document.dispatchEvent(new CustomEvent('pf:analytics', { detail: detail }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: 'pf_' + eventName }, detail));
      }
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* Remember that this visitor used filters, so add-to-cart events can
     be attributed to a filtered session (read this key in your ATC
     tracking): sessionStorage "pf_filtered_state". */
  function rememberFilterState() {
    try {
      sessionStorage.setItem(
        'pf_filtered_state',
        JSON.stringify({
          collection: window.location.pathname,
          query: window.location.search,
          at: Date.now()
        })
      );
    } catch (e) { /* private mode etc. */ }
  }

  /* ----------------------------------------------------------
     Mobile drawer
     The drawer lives inside a swapped region, so always query it
     fresh — never cache the element across swaps.
     ---------------------------------------------------------- */
  var lastFocused = null;

  function getDrawer() { return document.querySelector('[data-pf-drawer]'); }
  function getOverlay() { return document.querySelector('[data-pf-overlay]'); }
  function drawerIsOpen() {
    var d = getDrawer();
    return !!d && d.getAttribute('data-open') === 'true';
  }

  function openDrawer() {
    var d = getDrawer();
    if (!d) return;
    lastFocused = document.activeElement;
    d.setAttribute('data-open', 'true');
    var o = getOverlay();
    if (o) o.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    var closeBtn = d.querySelector('[data-pf-drawer-close]');
    if (closeBtn) closeBtn.focus();
    track('filter_drawer_open');
  }

  function closeDrawer() {
    var d = getDrawer();
    if (!d) return;
    d.setAttribute('data-open', 'false');
    var o = getOverlay();
    if (o) o.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-pf-drawer-open]')) {
      e.preventDefault();
      openDrawer();
    }
    if (e.target.closest('[data-pf-drawer-close]') || e.target === getOverlay()) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerIsOpen()) {
      closeDrawer();
    }
  });

  /* ----------------------------------------------------------
     Collapsible filter groups (works with the [data-expanded]
     attribute the Liquid renders; state kept per group per page).
     ---------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-pf-filter-toggle]');
    if (!toggle) return;
    var group = toggle.closest('.pf-filter');
    if (!group) return;
    var expanded = group.getAttribute('data-expanded') !== 'false';
    group.setAttribute('data-expanded', expanded ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  });

  /* ----------------------------------------------------------
     Fetch + swap filtering
     Builds the URL from the filter form, fetches the document, and
     swaps every [data-pf-region] region. Falls back to normal
     navigation on any error.
     ---------------------------------------------------------- */
  var fetchAbort = null;
  var debounceTimer = null;

  /* The form is inside the swapped "filters" region — query it fresh
     every time instead of caching a node that gets replaced. */
  function getForm() { return document.querySelector('[data-pf-filter-form]'); }

  function currentFormUrl(extraParams) {
    var form = getForm();
    var base = form ? form.getAttribute('action') : window.location.pathname;
    var params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams(window.location.search);
    // Drop empty range fields so URLs stay clean/shareable.
    var keys = [];
    params.forEach(function (value, key) {
      if (value === '') keys.push(key);
    });
    keys.forEach(function (k) {
      var kept = params.getAll(k).filter(function (v) { return v !== ''; });
      params.delete(k);
      kept.forEach(function (v) { params.append(k, v); });
    });
    if (extraParams) {
      Object.keys(extraParams).forEach(function (k) {
        params.delete(k);
        if (extraParams[k] !== null && extraParams[k] !== '') params.set(k, extraParams[k]);
      });
    }
    var qs = params.toString();
    return base + (qs ? '?' + qs : '');
  }

  function swapRegions(doc) {
    // The drawer lives inside the filters region: preserve its open
    // state across the swap so mobile filtering doesn't slam it shut.
    var wasOpen = drawerIsOpen();
    var newRegions = doc.querySelectorAll(REGION_SELECTOR);
    newRegions.forEach(function (fresh) {
      var name = fresh.getAttribute('data-pf-region');
      var current = document.querySelector('[data-pf-region="' + name + '"]');
      if (current) current.innerHTML = fresh.innerHTML;
    });
    if (wasOpen) {
      var d = getDrawer();
      var o = getOverlay();
      if (d) d.setAttribute('data-open', 'true');
      if (o) o.setAttribute('data-open', 'true');
    }
  }

  function applyUrl(url, push) {
    if (!('fetch' in window)) {
      window.location.href = url;
      return;
    }
    if (fetchAbort) fetchAbort.abort();
    fetchAbort = new AbortController();
    document.documentElement.classList.add('pf-is-loading');

    fetch(url, { signal: fetchAbort.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        swapRegions(doc);
        if (push) history.pushState({ pf: true }, '', url);
        document.documentElement.classList.remove('pf-is-loading');
        rememberFilterState();
        var grid = document.querySelector('[data-pf-region="grid"]');
        if (grid && grid.querySelector('[data-pf-empty]')) {
          track('filter_zero_results', { url: url });
        }
        document.dispatchEvent(new CustomEvent('pf:results-updated'));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        document.documentElement.classList.remove('pf-is-loading');
        window.location.href = url; // graceful fallback
      });
  }

  // Auto-apply on any checkbox change; debounce range/number inputs.
  // Delegated at document level because the form is replaced on swap.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input.name || !input.closest('[data-pf-filter-form]')) return;
    var isRange = input.getAttribute('data-pf-range') !== null;
    var run = function () {
      track('filter_apply', {
        filter_param: input.name,
        filter_value: input.value,
        checked: input.type === 'checkbox' ? input.checked : null
      });
      applyUrl(currentFormUrl(), true);
    };
    if (isRange) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(run, 500);
    } else {
      run();
    }
  });

  document.addEventListener('submit', function (e) {
    if (!e.target.closest || !e.target.hasAttribute('data-pf-filter-form')) return;
    e.preventDefault();
    track('filter_apply', { via: 'submit' });
    applyUrl(currentFormUrl(), true);
    closeDrawer();
  });

  // Pills, "clear all", pagination and sort are links / selects that
  // live inside swapped regions — handle by delegation.
  document.addEventListener('click', function (e) {
    var pill = e.target.closest('[data-pf-pill-remove]');
    if (pill) {
      e.preventDefault();
      track('filter_pill_remove', { label: pill.getAttribute('data-pf-pill-label') || '' });
      applyUrl(pill.getAttribute('href'), true);
      syncFormToUrl(pill.getAttribute('href'));
      return;
    }
    var clear = e.target.closest('[data-pf-clear-all]');
    if (clear) {
      e.preventDefault();
      track('filter_clear_all');
      applyUrl(clear.getAttribute('href'), true);
      syncFormToUrl(clear.getAttribute('href'));
      closeDrawer();
      return;
    }
    var page = e.target.closest('[data-pf-region="grid"] .pf-pagination a');
    if (page) {
      e.preventDefault();
      applyUrl(page.getAttribute('href'), true);
      var gridTop = document.querySelector('[data-pf-region="grid"]');
      if (gridTop) gridTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    var project = e.target.closest('[data-pf-project]');
    if (project) {
      // Let navigation happen (project chips may cross collections),
      // just record it first.
      track('shop_by_project_click', {
        project: project.getAttribute('data-pf-project'),
        href: project.getAttribute('href')
      });
    }
  });

  document.addEventListener('change', function (e) {
    var sort = e.target.closest('[data-pf-sort]');
    if (!sort) return;
    track('sort_change', { sort_by: sort.value });
    applyUrl(currentFormUrl({ sort_by: sort.value }), true);
  });

  /* Keep the form's checkboxes in sync after a pill/clear-all removal
     (regions swap, but the sidebar form inputs also live in a region,
     so re-sync is only needed when the form itself was NOT swapped). */
  function syncFormToUrl(url) {
    var form = getForm();
    if (!form) return;
    var params = new URLSearchParams((url.split('?')[1] || ''));
    form.querySelectorAll('input[type="checkbox"]').forEach(function (box) {
      box.checked = params.getAll(box.name).indexOf(box.value) !== -1;
    });
    form.querySelectorAll('input[data-pf-range]').forEach(function (field) {
      field.value = params.get(field.name) || '';
    });
  }

  window.addEventListener('popstate', function () {
    applyUrl(window.location.pathname + window.location.search, false);
    syncFormToUrl(window.location.search);
  });

  // Flag initial state for zero-result tracking on landing.
  if (document.querySelector('[data-pf-region="grid"] [data-pf-empty]') && window.location.search.indexOf('filter.') !== -1) {
    track('filter_zero_results', { url: window.location.href, on_load: true });
  }
  if (window.location.search.indexOf('filter.') !== -1) {
    rememberFilterState();
  }
})();
